import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import { getCompleteContactList, getOrganizationByID, downloadOrgDocument, getOrgDocuments, getOrganization, patchContactStatus, putOrganizationDocument, getOrganizationIssuersByOrgId, getOrganizationContactsByOrgId, getOrganizationClientsByOrgId, updateOrganizationByOrgId, createNewIssuers, putContactForOrg, updateOrganizationIssuerById, patchIssuerStatus, postOrganization, uploadOrgDocuments, patchWorkDayWithhold, checkIssuerCodeDuplicate } from './cruds';

export const ACTION_TYPE_PARENT = 'organization'

// NOTE: keep function name and thunk child name same
export const fetchOrganizations = createAsyncThunk(`${ACTION_TYPE_PARENT}/fetchOrganizations`,
  async ({ requestHeaders = undefined }: any, { rejectWithValue }) => {
    try {
      const response = await getOrganization(requestHeaders);
      return { data: response.data, responseHeaders: response.headers };
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }

  }
)

// * create organization
export const createOrganization = createAsyncThunk(`${ACTION_TYPE_PARENT}/createOrganization`,
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await postOrganization(payload);
      return response.data
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.response.data })
    }
  }
)

export const fetchOrganizationByID = createAsyncThunk(`${ACTION_TYPE_PARENT}/fetchOrganizationByID`,
  async (organizationId: number, { rejectWithValue }) => {
    try {
      const response = await getOrganizationByID(organizationId)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.response.data })
    }
  }
)

export const fetchCompleteContactList = createAsyncThunk(`${ACTION_TYPE_PARENT}/fetchCompleteContactList`,
  async ({ payload, params, requestHeaders = undefined }: any, { rejectWithValue }) => {
    try {
      const response = await getCompleteContactList(payload, params, requestHeaders)
      return { data: response.data, responseHeaders: response.headers };
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

// * patch update the status of contact
export const patchUpdateContactStatus = createAsyncThunk(`${ACTION_TYPE_PARENT}/patchUpdateContactStatus`,
  async ({ organizationId, contactId, params }: any, { rejectWithValue }) => {
    try {
      const response = await patchContactStatus(organizationId, contactId, params)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

// * returns all the organization documents
export const fetchOrganizationDocuments = createAsyncThunk(`${ACTION_TYPE_PARENT}/fetchOrganizationDocuments`,
  async ({ organizationPk, requestHeaders }: any, { rejectWithValue }) => {
    try {
      const response = await getOrgDocuments(organizationPk, requestHeaders)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

// * download organization document
export const downloadOrganizationDocument = createAsyncThunk(`${ACTION_TYPE_PARENT}/downloadOrganizationDocument`,
  async ({ organizationPk, documentId, onDownloadProgress }: { organizationPk: number, documentId: string, onDownloadProgress: Function }, { rejectWithValue }) => {
    try {
      const response = await downloadOrgDocument(organizationPk, documentId, onDownloadProgress)
      return response;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

// * updates the comment on the organization document
export const updateOrganizationDocument = createAsyncThunk(`${ACTION_TYPE_PARENT}/updateOrganizationDocument`,
  async ({ organizationPk, documentId, params }: any, { rejectWithValue }) => {
    try {
      const response = await putOrganizationDocument(organizationPk, documentId, params)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

// * fetch organization issuers by organization ID
export const fetchOrganizationIssuersByOrgId = createAsyncThunk(`${ACTION_TYPE_PARENT}/fetchOrganizationIssuersByOrgId`,
  async ({ organizationPk, requestHeaders }: any, { rejectWithValue }) => {
    try {
      const response = await getOrganizationIssuersByOrgId(organizationPk, requestHeaders)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

// * fetch organization contacts by organization ID
export const fetchOrganizationContactsByOrgId = createAsyncThunk(`${ACTION_TYPE_PARENT}/fetchOrganizationContactsByOrgId`,
  async ({ organizationPk, requestHeaders }: any, { rejectWithValue }) => {
    try {
      const response = await getOrganizationContactsByOrgId(organizationPk, requestHeaders)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

// * fetch organization clients by organization ID
export const fetchOrganizationClientsByOrgId = createAsyncThunk(`${ACTION_TYPE_PARENT}/fetchOrganizationClientsByOrgId`,
  async ({ organizationPk, requestHeaders }: any, { rejectWithValue }) => {
    try {
      const response = await getOrganizationClientsByOrgId(organizationPk, requestHeaders)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err.message })
    }
  }
)

export const editOrganizationByOrgId = createAsyncThunk(`${ACTION_TYPE_PARENT}/editOrganizationByOrgId`,
  async ({ organizationPk, payload }: any, { rejectWithValue }) => {
    try {
      const response = await updateOrganizationByOrgId(organizationPk, payload)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err?.response?.data })
    }
  }
)

export const generateNewIssuer = createAsyncThunk(`${ACTION_TYPE_PARENT}/generateNewIssuer`,
  async ({ organizationPk, payload }: any, { rejectWithValue }) => {
    try {
      const response = await createNewIssuers(organizationPk, payload)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err?.response?.data })
    }
  }
)

export const editContactForOrg = createAsyncThunk(`${ACTION_TYPE_PARENT}/editContactForOrg`,
  async ({ organizationPk, contactId, payload }: any, { rejectWithValue }) => {
    try {
      const response = await putContactForOrg(organizationPk, contactId, payload)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err?.response?.data })
    }
  }
)

export const editOrganizationIssuerById = createAsyncThunk(`${ACTION_TYPE_PARENT}/editOrganizationIssuerById`,
  async ({ organizationPk, issuerId, payload }: any, { rejectWithValue }) => {
    try {
      const response = await updateOrganizationIssuerById(organizationPk, issuerId, payload)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err?.response?.data })
    }
  }
)

export const updateIssuerStatusByIssuerId = createAsyncThunk(`${ACTION_TYPE_PARENT}/updateIssuerStatusByIssuerId`,
  async ({ organizationId, issuerId, status }: any, { rejectWithValue }) => {
    try {
      const response = await patchIssuerStatus(organizationId, issuerId, status)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err?.response?.data })
    }
  }
)

export const updateWorkDayWithhold = createAsyncThunk(`${ACTION_TYPE_PARENT}/updateWorkDayWithhold`,
  async ({ organizationId, issuerId, exclude }: any, { rejectWithValue }) => {
    try {
      const response = await patchWorkDayWithhold(organizationId, issuerId, exclude)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err?.response?.data })
    }
  }
)

export const uploadOrganizationDocuments = createAsyncThunk(`${ACTION_TYPE_PARENT}/uploadOrganizationDocuments`,
  async ({ organizationPk, docType, comment, fileInput }: any, { rejectWithValue }) => {
    try {
      const response = await uploadOrgDocuments(organizationPk, docType, comment, fileInput)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err?.response?.data })
    }
  }
)

export const verifyIssuerCodeDuplicate = createAsyncThunk(`${ACTION_TYPE_PARENT}/verifyIssuerCodeDuplicate`,
  async ({ organizationPk, issuerId, code }: any, { rejectWithValue }) => {
    try {
      const response = await checkIssuerCodeDuplicate(organizationPk, issuerId, code)
      return response.data;
    }
    catch (err: any) {
      if (!err.response) {
        throw err
      }
      return rejectWithValue({ status: err.response.status, code: err.code, message: err?.response?.data })
    }
  }
)
